import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from "react";
import { AuthContext } from "../auth-context";
import { useAsyncFn } from "../../hooks/use-async-fn";
import { useOrgScope, serializeFromOrganization } from "./OrgScopeContext";
import type {
  EventTypeRecord,
  EventMonthRecord,
  FederationMonthRecord,
  AdserverMonthRecord,
  ActiveFederationRecord,
  DashboardMeta,
} from "./DashboardContext";

// Maps the raw "Error <status>" thrown by handleError to copy a user can act on.
const friendlyDashboardError = (error: unknown): string => {
  const status = Number(String(error).match(/\d+/)?.[0]);
  switch (status) {
    case 401:
      return "Your session has expired. Please sign in again.";
    case 403:
      return "You don't have permission to view this organization's dashboard.";
    case 404:
      return "No dashboard data was found for this organization.";
    default:
      return "Failed to load the organization dashboard. Please try again.";
  }
};

// Activation/Enrichment record for org-scoped API: no organization_id/name fields
export interface OrgActivationEnrichmentRecord extends EventMonthRecord {
  activation_enrichment: "activation" | "enrichment";
}

export interface OrgDashboardData {
  eventType: EventTypeRecord[];
  eventMonth: EventMonthRecord[];
  federationMonth: FederationMonthRecord[];
  organisationMonth: never[];
  adserverMonth: AdserverMonthRecord[];
  orgActivationEnrichmentMonth: OrgActivationEnrichmentRecord[];
  activeFederations: ActiveFederationRecord[];
  uniqueActiveFederationCount: number | null;
}

export interface OrgDashboardMeta extends DashboardMeta {
  organization_id?: string;
  organization_name?: string | null;
  last_refreshed_at?: string;
  from_organization?: boolean | null;
}

interface OrgDashboardResponse {
  data: Omit<OrgDashboardData, "organisationMonth" | "uniqueActiveFederationCount">;
  meta: OrgDashboardMeta;
}

interface OrgDashboardContextType {
  dashboardData: OrgDashboardData | null;
  meta: OrgDashboardMeta | null;
  isLoading: boolean;
  isFulfilled: boolean;
  error: string | null;
  dataErrors: Partial<Record<keyof OrgDashboardData, boolean>>;
  lastRefreshedAt: Date | null;
  refetch: (startDate: string, endDate: string, keys?: string[]) => void;
}

export const OrgDashboardContext = createContext<OrgDashboardContextType | undefined>(
  undefined,
);

export const OrgDashboardProvider = ({ children }: { children: ReactNode }) => {
  const { orgGetDashboard } = useContext(AuthContext);
  const { orgId, fromOrganization } = useOrgScope();
  const getDashboardFn = useAsyncFn({ deferFn: orgGetDashboard });
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);

  // Store the current date range so we can re-fetch with the same range when org changes
  const dateRangeRef = useRef<{ start: string; end: string } | null>(null);

  const fmtLocal = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  // Fetch (or re-fetch) for the given org and date range
  const fetchForOrg = (id: string, start: string, end: string, keys?: string[]) => {
    if (!id) return;
    dateRangeRef.current = { start, end };
    // getDashboardFn.run() rejects on error (in addition to recording it on
    // state) — catch here so a failed fetch doesn't surface as an unhandled
    // promise rejection; getDashboardFn.error/isRejected is what callers use.
    getDashboardFn.run({
      org_id: id,
      start_date: start,
      end_date: end,
      keys,
      from_organization: serializeFromOrganization(fromOrganization),
    }).catch(() => {});
  };

  // Initial fetch on mount
  useEffect(() => {
    if (!orgId) return;
    const now = new Date();
    const start = fmtLocal(
      new Date(now.getFullYear(), now.getMonth() - 3, now.getDate()),
    );
    const end = fmtLocal(now);
    fetchForOrg(orgId, start, end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch when the selected org changes (admin switches org)
  const prevOrgIdRef = useRef(orgId);
  useEffect(() => {
    if (orgId === prevOrgIdRef.current) return;
    prevOrgIdRef.current = orgId;
    if (!orgId) return;
    const { start, end } = dateRangeRef.current ?? (() => {
      const now = new Date();
      return {
        start: fmtLocal(new Date(now.getFullYear(), now.getMonth() - 3, now.getDate())),
        end: fmtLocal(now),
      };
    })();
    fetchForOrg(orgId, start, end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  // Re-fetch when the Initiated/Target tab changes
  const prevFromOrgRef = useRef(fromOrganization);
  useEffect(() => {
    if (fromOrganization === prevFromOrgRef.current) return;
    prevFromOrgRef.current = fromOrganization;
    if (!orgId) return;
    const { start, end } = dateRangeRef.current ?? (() => {
      const now = new Date();
      return {
        start: fmtLocal(new Date(now.getFullYear(), now.getMonth() - 3, now.getDate())),
        end: fmtLocal(now),
      };
    })();
    fetchForOrg(orgId, start, end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromOrganization]);

  useEffect(() => {
    if (getDashboardFn.isFulfilled) {
      setLastRefreshedAt(new Date());
    }
  }, [getDashboardFn.isFulfilled, getDashboardFn.data]);

  const response: OrgDashboardResponse | undefined = getDashboardFn.data;
  const raw = response?.data;
  const dataErrors: Partial<Record<keyof OrgDashboardData, boolean>> = {};
  const safeArr = (val: any, key: keyof OrgDashboardData): any[] => {
    if (Array.isArray(val)) return val;
    if (val !== null && val !== undefined) dataErrors[key] = true;
    return [];
  };
  const dashboardData: OrgDashboardData | null = raw
    ? {
        eventType: safeArr(raw.eventType, "eventType"),
        eventMonth: safeArr(raw.eventMonth, "eventMonth"),
        federationMonth: safeArr(raw.federationMonth, "federationMonth"),
        organisationMonth: [],
        adserverMonth: safeArr(raw.adserverMonth, "adserverMonth"),
        orgActivationEnrichmentMonth: safeArr(
          raw.orgActivationEnrichmentMonth,
          "orgActivationEnrichmentMonth",
        ),
        activeFederations: safeArr(raw.activeFederations, "activeFederations"),
        uniqueActiveFederationCount:
          (raw.activeFederations as any)?.summary
            ?.unique_active_federation_count ?? null,
      }
    : null;

  return (
    <OrgDashboardContext.Provider
      value={{
        dashboardData,
        meta: response?.meta ?? null,
        isLoading: getDashboardFn.isPending,
        isFulfilled: getDashboardFn.isFulfilled,
        error: getDashboardFn.isRejected
          ? friendlyDashboardError(getDashboardFn.error)
          : null,
        dataErrors,
        lastRefreshedAt,
        refetch: (startDate, endDate, keys) => {
          fetchForOrg(orgId, startDate, endDate, keys);
        },
      }}
    >
      {children}
    </OrgDashboardContext.Provider>
  );
};

export const useOrgDashboardContext = (): OrgDashboardContextType => {
  const ctx = useContext(OrgDashboardContext);
  if (!ctx)
    throw new Error(
      "useOrgDashboardContext must be used within an OrgDashboardProvider",
    );
  return ctx;
};
